import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  HiChevronDown,
  HiChevronRight, 
  HiPlay,
  HiDocumentText,
  HiFilter,
  HiDownload,
  HiRefresh,
  HiX,
} from "react-icons/hi";
import { useQuery, useQueryClient, QueryClient } from "@tanstack/react-query";
import { useQueryStates, parseAsString, parseAsInteger } from "nuqs";
import api from "../../services/api"; 
import { AudioPlayerModal } from "./AudioPlayerModal";
import { TranscriptModal } from "./TranscriptModal"; 
import { exportToCSV } from "../../utils/exportUtils";
import { formatIstDateTime, formatUtcDateTime } from "../../utils/dateTime";
import {
  CampaignContact,
  CallStatus,
} from "../../services/api";

interface ContactWithCallsProps {
  campaignId: string;
}

interface CallLog {
  id: number;
  campaign_id: string;
  campaign_contact_id: string;
  contact_name: string;
  phone_number: string;
  call_recording_url: string | null;
  call_transcript_url: string | null;
  duration: number;
  call_status: string;
  cost: number;
  analytics: any;
  created_at: string;
}

// Custom hook for fetching call logs with caching
const useContactCallLogs = (campaignId: string, contactId: string, enabled: boolean) => {
  return useQuery({
    queryKey: ['contactCallLogs', campaignId, contactId],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: "10",
        offset: "0",
        contact_id: contactId,
      });
      const response = await api.get(`/campaigns/${campaignId}/call-logs?${params}`);
      return response.data.call_logs as CallLog[];
    },
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
};

export const ContactsWithCalls: React.FC<ContactWithCallsProps> = ({ campaignId }) => {
  const [callStatusFilter, setCallStatusFilter] = useState<CallStatus | "">("");
  const [expandedContacts, setExpandedContacts] = useState<Set<string>>(new Set());
  // Debounced search handling: searchInput is immediate, search is debounced value used for queries
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();

  // Query parameters for modals and pagination
  const [{ page, contactId, callLogId, modalType }, setModalParams] = useQueryStates({
    page: parseAsInteger.withDefault(1).withOptions({
      shallow: false,
      scroll: false,
      history: 'replace',
      clearOnDefault: true
    }),
    contactId: parseAsString.withDefault("").withOptions({
      shallow: false,
      scroll: false,
      history: 'replace',
      clearOnDefault: true
    }),
    callLogId: parseAsInteger.withDefault(0).withOptions({
      shallow: false,
      scroll: false,
      history: 'replace',
      clearOnDefault: true
    }),
    modalType: parseAsString.withDefault("").withOptions({
      shallow: false,
      scroll: false,
      history: 'replace',
      clearOnDefault: true
    })
  });

  // Modal states
  const [selectedLog, setSelectedLog] = useState<CallLog | null>(null);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  const pageSize = 20;

  // Debounce effect for search input
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // When debounced search changes, reset to first page (skip initial mount)
  const didInitSearchRef = useRef(false);
  useEffect(() => {
    if (!didInitSearchRef.current) {
      didInitSearchRef.current = true;
      return;
    }
    setModalParams({ page: 1 });
  }, [search, setModalParams]);

  type ContactsData = { contacts: CampaignContact[]; total: number; hasMore: boolean };

  // Use React Query for fetching contacts
  const { data: contactsData, isLoading: loading, isFetching } = useQuery<ContactsData>({
    queryKey: ['campaignContacts', campaignId, page, callStatusFilter, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: ((page - 1) * pageSize).toString(),
      });
      
      if (callStatusFilter) {
        params.append("call_status", callStatusFilter);
      }
      if (search && search.trim() !== '') {
        params.append('search', search.trim());
      }

      const response = await api.get(`/campaigns/${campaignId}/contacts?${params}`);
      return {
        contacts: response.data.contacts as CampaignContact[],
        total: response.data.total as number,
        hasMore: response.data.has_more as boolean,
      };
    },
    placeholderData: (previousData) => previousData as ContactsData | undefined,
  });

  const contacts = useMemo(() => contactsData?.contacts || [], [contactsData?.contacts]);
  const total = contactsData?.total || 0;
  const hasMore = contactsData?.hasMore || false;

  // Effect to handle deep linking - open modal based on URL parameters
  useEffect(() => {
    if (!campaignId) {
      return;
    }
    
    if (callLogId && callLogId > 0 && modalType && (modalType === 'recording' || modalType === 'transcript')) {
      // Only fetch if we don't already have the correct modal open
      const isCorrectModalAlready = (
        (modalType === 'recording' && showAudioPlayer && selectedLog?.id === callLogId) ||
        (modalType === 'transcript' && showTranscript && selectedLog?.id === callLogId)
      );

      if (isCorrectModalAlready) {
        return; // Don't fetch if we already have the correct modal open
      }

      // Fetch the call log data for the deep link
      const fetchCallLogForDeepLink = async () => {
        try {
          const params = new URLSearchParams({
            limit: "100",
            offset: "0",
          });
          if (contactId) {
            params.set("contact_id", contactId);
          }
          const response = await api.get(`/campaigns/${campaignId}/call-logs?${params}`);
          const callLogs = response.data.call_logs as CallLog[];
          const callLog = callLogs.find(log => log.id === callLogId);
          
          if (!callLog) {
            throw new Error(`Call log with ID ${callLogId} not found`);
          }
          
          setSelectedLog(callLog);
          
          if (modalType === 'recording') {
            setShowAudioPlayer(true);
            setShowTranscript(false);
          } else if (modalType === 'transcript') {
            setShowTranscript(true);
            setShowAudioPlayer(false);
          }
          
          // Expand the contact if contactId is provided
          if (contactId && contactId.trim() !== '') {
            setExpandedContacts(prev => new Set([...Array.from(prev), contactId]));
          }
        } catch (error) {
          console.error('Error fetching call log for deep link:', error);
          // Clear the query params if the call log doesn't exist
          setModalParams({ contactId: "", callLogId: 0, modalType: "" });
        }
      };

      fetchCallLogForDeepLink();
    }
  }, [callLogId, modalType, contactId, campaignId, setModalParams, showAudioPlayer, showTranscript, selectedLog]);

  const toggleContactExpanded = (contactId: string) => {
    const newExpanded = new Set(expandedContacts);
    
    if (newExpanded.has(contactId)) {
      newExpanded.delete(contactId);
    } else {
      newExpanded.add(contactId);
    }
    
    setExpandedContacts(newExpanded);
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `0:${seconds.toString().padStart(2, "0")}`;
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const formatTime = (dateString: string): string => {
    return formatUtcDateTime(dateString, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit", 
      minute: "2-digit",
      second: undefined,
      hour12: true,
    });
  };

  // Helper function to generate page numbers for pagination
  const generatePageNumbers = (currentPage: number, totalPages: number) => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 7; // Maximum number of page buttons to show
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust range if current page is near the beginning
      if (currentPage <= 3) {
        endPage = 4;
      }
      
      // Adjust range if current page is near the end
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
      }
      
      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push('...');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        if (i > 1 && i < totalPages) {
          pages.push(i);
        }
      }
      
      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  const getCallStatusBadge = (status: CallStatus) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case CallStatus.PENDING:
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case CallStatus.CALLING:
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case CallStatus.COMPLETED:
        return `${baseClasses} bg-green-100 text-green-800`;
      case CallStatus.FAILED:
        return `${baseClasses} bg-red-100 text-red-800`;
      case CallStatus.SKIPPED:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case "completed":
      case "successful":
        return "bg-green-100 text-green-800";
      case "missed":
      case "rejected":
      case "refused":
        return "bg-red-100 text-red-800";
      case "callback scheduled":
        return "bg-yellow-100 text-yellow-800";
      case "promise to pay":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const capitalizeFirstLetter = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const handlePlayAudio = (log: CallLog) => {
    setSelectedLog(log);
    setShowAudioPlayer(true);
    // Set query parameters for deep linking
    setModalParams({
      contactId: log.campaign_contact_id,
      callLogId: log.id,
      modalType: 'recording'
    });
  };

  const handleViewTranscript = (log: CallLog) => {
    setSelectedLog(log);
    setShowTranscript(true);
    // Set query parameters for deep linking
    setModalParams({
      contactId: log.campaign_contact_id,
      callLogId: log.id,
      modalType: 'transcript'
    });
  };

  const handleDownloadRecording = async (log: CallLog) => {
    try {
      const response = await api.get(`/campaigns/call-logs/${log.id}/download-url`);
      const { download_url, filename } = response.data;
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = download_url;
      link.download = filename || `recording_${log.id}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading recording:', error);
      // You might want to show a toast notification here
    }
  };

  const handleExport = () => {
    const exportData: any[] = [];
    
    contacts.forEach(contact => {
      // Add contact info
      exportData.push({
        Type: "Contact",
        Name: `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || contact.phone_number,
        "Phone Number": contact.phone_number,
        Status: contact.call_status,
        Attempts: contact.call_attempts,
        "Last Called": contact.last_called_at ? formatTime(contact.last_called_at) : "Never",
        Result: contact.call_result || "-",
      });
    });

    exportToCSV(exportData, `contacts-with-calls-${campaignId}-${new Date().toISOString().split('T')[0]}.csv`);
  };

    // Skeleton loader component with wave animation
  const CallLogSkeleton = () => (
    <div className="space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-gray-100 rounded-lg p-3 relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div className="flex-1 grid grid-cols-4 gap-4 items-center">
              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-24" style={{animationDelay: '0s', animationDuration: '1.5s'}}></div>
              <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-20" style={{animationDelay: '0.2s', animationDuration: '1.5s'}}></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-16" style={{animationDelay: '0.4s', animationDuration: '1.5s'}}></div>
              <div className="flex items-center justify-end space-x-3">
                <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-12" style={{animationDelay: '0.6s', animationDuration: '1.5s'}}></div>
                <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-16" style={{animationDelay: '0.8s', animationDuration: '1.5s'}}></div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Contact skeleton loader for main table
  const ContactSkeleton = () => (
    <div className="space-y-1">
      {[1, 2, 3].map((i) => (
        <div key={i} className="px-6 py-4">
          <div className="flex items-center space-x-4">
            <div className="w-6 h-4 bg-gray-200 rounded"></div>
            <div className="flex-1 grid grid-cols-7 gap-4 items-center">
              <div className="col-span-2 space-y-2">
                <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-32" style={{animationDelay: `${i * 0.1}s`, animationDuration: '1.5s'}}></div>
                <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-24" style={{animationDelay: `${i * 0.1 + 0.1}s`, animationDuration: '1.5s'}}></div>
              </div>
              <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-20" style={{animationDelay: `${i * 0.1 + 0.2}s`, animationDuration: '1.5s'}}></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-8" style={{animationDelay: `${i * 0.1 + 0.3}s`, animationDuration: '1.5s'}}></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-16" style={{animationDelay: `${i * 0.1 + 0.4}s`, animationDuration: '1.5s'}}></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-12" style={{animationDelay: `${i * 0.1 + 0.5}s`, animationDuration: '1.5s'}}></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-10" style={{animationDelay: `${i * 0.1 + 0.6}s`, animationDuration: '1.5s'}}></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Note: Do not return early on loading to preserve header/search focus

  return (
    <div className="bg-[var(--card)] rounded-lg shadow-sm border border-[var(--border)]">
      <div className="px-6 py-4 border-b border-[var(--border)]">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[var(--foreground)]">Campaign Contacts</h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <HiFilter className="text-gray-400 h-5 w-5" />
              <select
                value={callStatusFilter}
                onChange={(e) => setCallStatusFilter(e.target.value as CallStatus | "")}
                className="border border-[var(--border)] rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-accent focus:border-transparent text-[var(--foreground)] bg-[var(--primary-foreground)]"
              >
                <option value="">All Status</option>
                <option value={CallStatus.PENDING}>Pending</option>
                <option value={CallStatus.CALLING}>Calling</option>
                <option value={CallStatus.COMPLETED}>Completed</option>
                <option value={CallStatus.FAILED}>Failed</option>
                <option value={CallStatus.SKIPPED}>Skipped</option>
                <option value={CallStatus.RESCHEDULED}>Rescheduled</option>
              </select>
            </div>
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                }}
                placeholder="Search name or phone"
                className="border border-[var(--border)] rounded-lg pl-3 pr-8 py-2 text-sm focus:ring-2 focus:ring-accent focus:border-transparent text-[var(--foreground)] bg-[var(--primary-foreground)] w-56"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    setSearch("");
                    // keep focus on the input after clearing
                    if (searchInputRef.current) {
                      searchInputRef.current.focus();
                    }
                  }}
                  aria-label="Clear search"
                  title="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--foreground-muted)] hover:text-[var(--foreground)]"
                >
                  <HiX className="h-4 w-4" />
                </button>
              )}
            </div>
            <button 
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['campaignContacts', campaignId] });
              }}
              className="text-primary hover:text-primary/80 flex items-center gap-2"
              title="Refresh data"
            >
              <HiRefresh className="h-5 w-5" />
              Refresh
            </button>
            <button 
              onClick={handleExport}
              className="text-primary hover:text-primary/80 flex items-center gap-2"
            >
              <HiDownload className="h-5 w-5" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Table Header */}
      <div className="px-6 py-3 bg-[var(--muted)] border-b border-[var(--border)]">
        <div className="flex items-center space-x-4">
          <div className="w-6"></div> {/* Space for expand icon */}
          <div className="flex-1 grid grid-cols-7 gap-4 items-center text-xs font-medium text-[var(--foreground-muted)] uppercase tracking-wider">
            <div className="col-span-2">Contact Information</div>
            <div>Status</div>
            <div>Attempts</div>
            <div>Last Called</div>
            {/* <div>Last Result</div>
            <div>Success Rate</div> */}
          </div>
        </div>
      </div>

      <div className="divide-y divide-[var(--border)]">
        {(loading || (isFetching && contacts.length === 0)) ? (
          <div className="px-6 py-4"><ContactSkeleton /></div>
        ) : contacts.map((contact) => {
          const isExpanded = expandedContacts.has(contact.id);
          
          // Create a custom component for each contact to use the hook properly
          return (
            <ContactRow
              key={contact.id}
              contact={contact}
              isExpanded={isExpanded}
              onToggle={() => toggleContactExpanded(contact.id)}
              formatTime={formatTime}
              formatDuration={formatDuration}
              getCallStatusBadge={getCallStatusBadge}
              getStatusColor={getStatusColor}
              capitalizeFirstLetter={capitalizeFirstLetter}
              onPlayAudio={handlePlayAudio}
              onViewTranscript={handleViewTranscript}
              onDownloadRecording={handleDownloadRecording}
              campaignId={campaignId}
              CallLogSkeleton={CallLogSkeleton}
              queryClient={queryClient}
            />
          );
        })}
      </div>

      {contacts.length === 0 && (
        <div className="text-center py-12">
          <div className="text-[var(--foreground-muted)]">No contacts found</div>
        </div>
      )}

      {/* Pagination */}
      {total > pageSize && (
        <div className="sticky bottom-0 bg-[var(--card)] px-6 py-4 border-t border-[var(--border)] shadow-lg z-10">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing page <span className="font-medium">{page}</span> of{" "}
                <span className="font-medium">{Math.ceil(total / pageSize)}</span>
              </p>
            </div>
            <div className="flex items-center space-x-1">
              {/* Previous Button */}
              <button
                onClick={() => setModalParams({ page: Math.max(1, page - 1) })}
                disabled={page === 1}
                className="px-3 py-2 border border-[var(--border)] rounded-lg text-sm font-medium text-[var(--foreground)] bg-[var(--card)] hover:bg-[var(--muted)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center space-x-1 mx-2">
                {generatePageNumbers(page, Math.ceil(total / pageSize)).map((pageNum, index) => (
                  <button
                    key={index}
                    onClick={() => typeof pageNum === 'number' ? setModalParams({ page: pageNum }) : null}
                    disabled={typeof pageNum !== 'number'}
                    className={`
                      px-3 py-2 text-sm font-medium rounded-lg transition-colors
                      ${typeof pageNum === 'number' && pageNum === page 
                        ? 'bg-accent text-white border border-accent' 
                        : typeof pageNum === 'number'
                          ? 'border border-[var(--border)] text-[var(--foreground)] bg-[var(--card)] hover:bg-[var(--muted)]'
                          : 'text-[var(--foreground-muted)] cursor-default'
                      }
                      ${typeof pageNum !== 'number' ? 'px-2' : ''}
                    `}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              {/* Next Button */}
              <button
                onClick={() => setModalParams({ page: page + 1 })}
                disabled={!hasMore}
                className="px-3 py-2 border border-[var(--border)] rounded-lg text-sm font-medium text-[var(--foreground)] bg-[var(--card)] hover:bg-[var(--muted)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAudioPlayer && selectedLog && (
        <AudioPlayerModal
          callLog={selectedLog}
          onClose={() => {
            setShowAudioPlayer(false);
            setSelectedLog(null);
            // Clear query parameters
            setModalParams({ contactId: "", callLogId: 0, modalType: "" });
          }}
        />
      )}

      {showTranscript && selectedLog && (
        <TranscriptModal
          callLog={selectedLog}
          onClose={() => {
            setShowTranscript(false);
            setSelectedLog(null);
            // Clear query parameters
            setModalParams({ contactId: "", callLogId: 0, modalType: "" });
          }}
        />
      )}
    </div>
  );
};

// Separate component for contact row to properly use React Query hook
interface ContactRowProps {
  contact: CampaignContact;
  isExpanded: boolean;
  onToggle: () => void;
  formatTime: (date: string) => string;
  formatDuration: (seconds: number) => string;
  getCallStatusBadge: (status: CallStatus) => string;
  getStatusColor: (status: string) => string;
  capitalizeFirstLetter: (str: string) => string;
  onPlayAudio: (log: CallLog) => void;
  onViewTranscript: (log: CallLog) => void;
  onDownloadRecording: (log: CallLog) => void;
  campaignId: string;
  CallLogSkeleton: () => JSX.Element;
  queryClient: QueryClient;
}

const ContactRow: React.FC<ContactRowProps> = ({
  contact,
  isExpanded,
  onToggle,
  formatDuration,
  getCallStatusBadge,
  getStatusColor,
  capitalizeFirstLetter,
  onPlayAudio,
  onViewTranscript,
  onDownloadRecording,
  campaignId,
  CallLogSkeleton,
  queryClient,
}) => {
  const { data: callLogs = [], isLoading } = useContactCallLogs(campaignId, contact.id, isExpanded);

  const formatTime = (dateString: string): string => {
    return formatUtcDateTime(dateString, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit", 
      minute: "2-digit",
      second: undefined,
      hour12: true,
    });
  };

  const formatCallLogTime = (dateString: string): string => {
    return formatIstDateTime(dateString, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit", 
      minute: "2-digit",
      second: undefined,
      hour12: true,
    });
  };

  return (
    <div className="bg-[var(--card)]">
      {/* Contact Row */}
      <div
        className="px-6 py-4 cursor-pointer hover:bg-[var(--muted)] transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="flex items-center">
              {isExpanded ? (
                <HiChevronDown className="h-5 w-5 text-gray-400" />
              ) : (
                <HiChevronRight className="h-5 w-5 text-gray-400" />
              )}
            </div>
            <div className="flex-1 grid grid-cols-7 gap-4 items-center">
              <div className="col-span-2">
                <div className="text-sm font-medium text-[var(--foreground)]">
                  {contact.first_name
                    ? `${contact.first_name} ${contact?.last_name || ""}`
                    : contact.phone_number}
                </div>
                {contact.first_name && <div className="text-sm text-[var(--foreground-muted)]">{contact.phone_number}</div>}
              </div>
              <div>
                <span className={getCallStatusBadge(contact.call_status)}>
                  {contact.call_status.charAt(0).toUpperCase() + contact.call_status.slice(1)}
                </span>
              </div>
              <div className="text-sm text-[var(--foreground)]">
                {contact.call_attempts}
              </div>
              <div className="text-sm text-[var(--foreground-muted)]">
                {contact.last_called_at
                  ? formatTime(contact.last_called_at)
                  : "Never"}
              </div>
              {/* <div className="text-sm text-[var(--foreground-muted)]">
                {contact.call_result || "-"}
              </div>
              <div className="text-sm text-[var(--foreground)]">
                {contact.call_attempts > 0 
                  ? `${Math.round((contact.call_status === CallStatus.COMPLETED ? 1 : 0) / contact.call_attempts * 100)}%`
                  : "-"}
              </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Call Logs with background */}
      {isExpanded && (
        <div className="bg-[var(--muted)]/50 px-6 pb-4 border-t border-[var(--border)]">
          <div className="ml-9 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-[var(--foreground-muted)]">Call History</h4>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  queryClient.invalidateQueries({ 
                    queryKey: ['contactCallLogs', campaignId, contact.id] 
                  });
                }}
                className="text-[var(--foreground-muted)] hover:text-accent p-1 rounded"
                title="Refresh call history"
              >
                <HiRefresh className="h-4 w-4" />
              </button>
            </div>
            
            {isLoading ? (
              <CallLogSkeleton />
            ) : callLogs && callLogs.length > 0 ? (
              <div className="space-y-2">
                {callLogs?.map((log) => (
                  <div 
                    key={log.id} 
                    className="bg-[var(--card)] rounded-lg p-3 flex items-center justify-between border border-[var(--border)]"
                  >
                    <div className="flex-1 grid grid-cols-4 gap-4 items-center">
                      <div className="text-sm text-[var(--foreground-muted)]">
                        {formatCallLogTime(log.created_at)}
                      </div>
                      <div>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(log.call_status)}`}>
                          {capitalizeFirstLetter(log.call_status)}
                        </span>
                      </div>
                      <div className="text-sm text-[var(--foreground)]">
                        {formatDuration(log.duration)} mins
                      </div>
                      <div className="flex items-center justify-end space-x-3">
                        {log.call_recording_url && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onPlayAudio(log);
                              }}
                              className="text-[var(--foreground)] hover:text-accent flex items-center gap-1 text-sm"
                            >
                              <HiPlay className="h-4 w-4" />
                              Play
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDownloadRecording(log);
                              }}
                              className="text-[var(--foreground)] hover:text-accent flex items-center gap-1 text-sm"
                              title="Download recording"
                            >
                              <HiDownload className="h-4 w-4" />
                              Download
                            </button>
                          </>
                        )}
                        {log.call_transcript_url && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewTranscript(log);
                            }}
                            className="text-[var(--foreground)] hover:text-accent flex items-center gap-1 text-sm"
                          >
                            <HiDocumentText className="h-4 w-4" />
                            Transcript
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="text-sm text-[var(--foreground-muted)]">No call logs available</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}; 
